/**
 * Validazione formato codice fiscale italiano.
 *
 * Decisione D7 (utente, 2026-05-12): inizialmente si era scelta una
 * libreria esterna, ma le opzioni disponibili (`@marketto/codice-
 * fiscale-utils`) richiedevano un Belfiore connector pesante anche
 * solo per validare il formato. Implementazione algoritmica MEF
 * autocontenuta (~70 LOC), zero dipendenze, allineata al DM 23/12/1976.
 *
 * Verifica:
 *  - pattern: 6 lettere (cognome+nome) + 2 cifre/lettere omocodia
 *    (anno) + 1 lettera mese + 2 cifre/lettere (giorno) +
 *    1 lettera + 3 cifre/lettere (Belfiore) + 1 lettera (CRC)
 *  - mese alfabetico: A=gen B=feb C=mar D=apr E=mag H=giu L=lug
 *    M=ago P=set R=ott S=nov T=dic
 *  - omocodia: nelle posizioni 6,7,9,10,12,13,14 le cifre possono
 *    essere sostituite da L,M,N,P,Q,R,S,T,U,V (rispettivamente 0..9)
 *  - check digit (16o char) calcolato sui primi 15
 *
 * NON verifica corrispondenza anagrafica (es. CF -> persona reale).
 * Quella verifica richiede Sistema TS e non e' in scope PR4.
 */

const OMOCODIA_DECODE: Record<string, string> = {
  L: '0', M: '1', N: '2', P: '3', Q: '4', R: '5', S: '6', T: '7', U: '8', V: '9',
};

const MONTH_CHARS = 'ABCDEHLMPRST';

const CF_PATTERN = new RegExp(
  '^[A-Z]{6}' + // cognome + nome
    '[0-9LMNPQRSTUV]{2}' + // anno (omocodia)
    '[' + MONTH_CHARS + ']' + // mese
    '[0-9LMNPQRSTUV]{2}' + // giorno (omocodia)
    '[A-Z]' + // primo char Belfiore
    '[0-9LMNPQRSTUV]{3}' + // 3 char Belfiore (omocodia)
    '[A-Z]$' // CRC
);

const ODD_MAP: Record<string, number> = {
  '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
  A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21,
  K: 2, L: 4, M: 18, N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14,
  U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23,
};
const EVEN_MAP: Record<string, number> = {
  '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  A: 0, B: 1, C: 2, D: 3, E: 4, F: 5, G: 6, H: 7, I: 8, J: 9,
  K: 10, L: 11, M: 12, N: 13, O: 14, P: 15, Q: 16, R: 17, S: 18, T: 19,
  U: 20, V: 21, W: 22, X: 23, Y: 24, Z: 25,
};

function decodeOmocodia(cfPart: string): string {
  return [...cfPart].map((c) => OMOCODIA_DECODE[c] ?? c).join('');
}

function computeCheckDigit(cf15: string): string | null {
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const ch = cf15[i]!;
    const map = i % 2 === 0 ? ODD_MAP : EVEN_MAP; // posizione 1-based dispari = i pari
    const v = map[ch];
    if (v === undefined) return null;
    sum += v;
  }
  return String.fromCharCode(65 + (sum % 26));
}

export function isValidCodiceFiscale(cf: unknown): boolean {
  if (typeof cf !== 'string') return false;
  const normalized = cf.trim().toUpperCase();
  if (normalized.length !== 16) return false;
  if (!CF_PATTERN.test(normalized)) return false;

  // Mese deve corrispondere a uno dei 12 codici (regex gia lo garantisce).
  // Giorno: dopo decode omocodia deve essere 01-31 oppure 41-71 (donne).
  const decoded = decodeOmocodia(normalized);
  const giorno = parseInt(decoded.slice(9, 11), 10);
  if (!((giorno >= 1 && giorno <= 31) || (giorno >= 41 && giorno <= 71))) {
    return false;
  }

  const expected = computeCheckDigit(normalized.slice(0, 15));
  if (expected === null) return false;
  return expected === normalized[15];
}

export function normalizeCodiceFiscale(cf: string): string {
  return cf.trim().toUpperCase();
}
